<template>
  <div>
    <h1>{{ this.username }}'s recorded games</h1>
    <router-link :to="{ name: 'TrackGame' }">Track a game</router-link>
    <h2>Win percentage</h2>
    <p>Hover for details</p>
    <b-table
      :items="items"
      :fields="winRatioFields"
      responsive
      class="compact-table-sm"
      small
      :key="`user-games-winratio-grid-${this.username}`"
    >
      <template v-slot:thead-top>
        <b-tr>
          <b-th><span class="sr-only">Deck Name</span></b-th>
          <b-th :colspan="winRatioFields.length - 1" class="text-center"
            >Versus</b-th
          >
        </b-tr>
      </template>

      <template #head()="data">
        <div>{{ data.label }}</div>
      </template>

      <template #custom-foot="data">
        <b-tr>
          <b-th
            v-for="(col, idx) in data.fields"
            :key="`ratio-footer-id-${col.key}`"
            :class="[
              idx
                ? 'text-center'
                : 'b-table-sticky-column table-b-table-default',
              idx ? winRatioClass(col) : '',
            ]"
          >
            <span
              v-if="idx"
              :id="$id(`ratio-footer-${idx}`)"
              tabindex="0"
              :class="[col.total_games ? 'info-tooltip' : '']"
            >
              {{ formatWinRatio(col) }}
            </span>
            <span v-else> Total </span>
            <b-popover
              v-if="idx && col.total_games"
              :key="'popover-footer__' + idx"
              :target="$id(`ratio-footer-${idx}`)"
              triggers="hover focus"
              placement="auto"
              :title="
                col.key === 'total'
                  ? 'Total victories'
                  : `Victories vs. ${col.label}`
              "
            >
              Won {{ col.victories }} out of {{ col.total_games }} games
            </b-popover>
          </b-th>
        </b-tr>
      </template>

      <template #cell(name)="data">
        <router-link
          :to="{ name: 'UmDbDeck', params: { slug: data.item.slug } }"
        >
          {{ data.value }}
        </router-link>
      </template>

      <template #cell()="data">
        <span
          :id="$id(data.item.id + '__' + data.field.key)"
          tabindex="0"
          :class="[data.item[data.field.key].total_games ? 'info-tooltip' : '']"
          >{{ data.value }}</span
        >
        <b-popover
          v-if="data.item[data.field.key].total_games"
          :key="'popover-' + data.item.id + '__' + data.field.key"
          :target="$id(data.item.id + '__' + data.field.key)"
          triggers="hover focus"
          placement="auto"
          :title="
            data.field.key === 'total'
              ? `Victories as ${data.item.name}`
              : `As ${data.item.name} vs. ${data.field.label}`
          "
        >
          Won {{ data.item[data.field.key].victories }} out of
          {{ data.item[data.field.key].total_games }} games
        </b-popover>
      </template>
    </b-table>

    <h2>Games played</h2>
    <b-table
      :items="items"
      :fields="totalGamesFields"
      responsive
      class="compact-table-sm"
      small
      :key="`user-games-count-grid-${this.username}`"
    >
      <template v-slot:thead-top>
        <b-tr>
          <b-th><span class="sr-only">Deck Name</span></b-th>
          <b-th :colspan="winRatioFields.length - 1" class="text-center"
            >Versus</b-th
          >
        </b-tr>
      </template>

      <template #head()="data">
        <div>{{ data.label }}</div>
      </template>

      <template #cell(name)="data">
        <router-link
          :to="{ name: 'UmDbDeck', params: { slug: data.item.slug } }"
        >
          {{ data.value }}
        </router-link>
      </template>

      <template #custom-foot="data">
        <b-tr>
          <b-th
            v-for="(col, idx) in data.fields"
            :key="`count-footer-id-${col.key}`"
            :class="[
              idx
                ? 'text-center'
                : 'b-table-sticky-column table-b-table-default',
              idx ? totalVsClass(col.total_games) : '',
            ]"
          >
            <template v-if="idx">
              {{ col.total_games }}
            </template>
            <template v-else> Total </template>
          </b-th>
        </b-tr>
      </template>
    </b-table>

    <h2>Games list</h2>
    <GameList
      :items="getGameData"
      :rows="rows"
      :key="`user-gamelist-${this.username}`"
      @refresh="populateGrid(username)"
    />
  </div>
</template>

<script>
import api from "@/api";
import GameList from "@/components/GameList.vue";

export default {
  name: "UserResultsGrid",
  metaInfo() {
    const title = this.username
      ? `${this.username}'s Results | UmDb`
      : undefined;
    return {
      title,
    };
  },
  props: {
    username: null,
  },
  components: { GameList },
  data() {
    return {
      items: [],
      winRatioFields: [],
      totalGamesFields: [],
      maxTotalGames: 0,
      maxTotalVs: 0,
      rows: -1,
    };
  },
  methods: {
    getGameData(ctx, callback) {
      api
        .getUserGames(this.username, ctx)
        .then((data) => {
          this.rows = data.total;
          callback(data.games);
        })
        .catch(() => {
          callback([]);
        });

      // Must return null or undefined to signal b-table that callback is being used
      return null;
    },
    formatWinRatio(value) {
      if (value.victories !== null && value.total_games > 0) {
        return (value.victories / value.total_games).toLocaleString("en", {
          style: "percent",
        });
      }
      return "-";
    },
    winRatioClass(value) {
      if (value.victories !== null) {
        const winRatio = value.victories / value.total_games;
        return `resultgrid-bg${Math.round(winRatio * 10)}`;
      }
      return null;
    },
    totalGamesClass(val) {
      return this.logClass(val, this.maxTotalGames);
    },
    totalTotalClass(val) {
      return this.logClass(val, this.maxTotalTotal);
    },
    totalVsClass(val) {
      return this.logClass(val, this.maxTotalVs);
    },
    logClass(val, max) {
      if (typeof val !== "number" || val === 0) return null;
      if (max === 1) return "resultgrid-bg10";
      const logVal = Math.log(val);
      const logMax = Math.log(max);
      return `resultgrid-bg${Math.round((logVal / logMax) * 10)}`;
    },
    populateGrid(username) {
      api.getUserResultsGrid(username).then((data) => {
        this.items = data.grid;
        this.maxTotalGames = data.max_total_games;
        this.maxTotalTotal = data.max_total_total;
        const labels = data.labels;
        const winRatioFields = [
          {
            key: "name",
            label: username,
            sortable: false,
            stickyColumn: true,
            class: ["truncate-col"],
          },
        ];
        const totalGamesFields = [
          {
            key: "name",
            label: username,
            sortable: false,
            stickyColumn: true,
            class: ["truncate-col"],
          },
        ];

        labels.forEach((label) => {
          const totalGames = data.grid.reduce((total, row) => {
            return total + (row[label.key].total_games || 0);
          }, 0);
          const totalVictories = data.grid.reduce((total, row) => {
            return total + (row[label.key].victories || 0);
          }, 0);

          this.maxTotalVs = Math.max(this.maxTotalVs, totalGames);

          winRatioFields.push({
            key: `${label.key}`,
            label: label.label,
            formatter: "formatWinRatio",
            thClass: "resultgrid-vertical",
            tdClass: this.winRatioClass,
            class: "text-center",
            slug: label.slug,
            total_games: totalGames,
            victories: totalVictories,
          });
          totalGamesFields.push({
            key: `${label.key}.total_games`,
            label: label.label,
            thClass: "resultgrid-vertical",
            tdClass: this.totalGamesClass,
            class: "text-center",
            slug: label.slug,
            total_games: totalGames,
          });
        });

        winRatioFields.push({
          key: "total",
          formatter: "formatWinRatio",
          thClass: "resultgrid-vertical",
          tdClass: this.winRatioClass,
          class: "text-center font-weight-bold",
          total_games: data.total_games,
          victories: data.total_victories,
        });
        totalGamesFields.push({
          key: "total.total_games",
          label: "Total",
          thClass: "resultgrid-vertical",
          tdClass: this.totalTotalClass,
          class: "text-center font-weight-bold",
          total_games: data.total_games,
        });

        this.winRatioFields = winRatioFields;
        this.totalGamesFields = totalGamesFields;
      });
    },
  },
  mounted() {
    this.populateGrid(this.username);
  },
  watch: {
    username(value) {
      this.populateGrid(value);
    },
  },
};
</script>

<style>
thead tr th.resultgrid-vertical div {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  margin: 0 auto;
  white-space: nowrap;
}
/*
    Colourblindness friendly palette from
    https://gka.github.io/palettes/#/11|d|005a74,3aa794,e7e3bc|e7e3bc,eda34e,db4325|1|1

    Text contrast checked here: https://webaim.org/resources/contrastchecker/;
*/

.resultgrid-bg10 {
  background-color: #005a74;
  color: #fff;
}
.resultgrid-bg9 {
  background-color: #2a7781;
  color: #fff;
}
.resultgrid-bg8 {
  background-color: #51948f;
  color: #000;
}
.resultgrid-bg7 {
  background-color: #7db09d;
  color: #000;
}
.resultgrid-bg6 {
  background-color: #afcaac;
  color: #000;
}
.resultgrid-bg5 {
  background-color: #e7e7bc;
  color: #000;
}
.resultgrid-bg4 {
  background-color: #edc68e;
  color: #000;
}
.resultgrid-bg3 {
  background-color: #eda869;
  color: #000;
}
.resultgrid-bg2 {
  background-color: #e9894c;
  color: #000;
}
.resultgrid-bg1 {
  background-color: #e36935;
  color: #000;
}
.resultgrid-bg0 {
  background-color: #db4325;
  color: #000;
}
</style>
